import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useQuery } from "react-query";
import { CompanyServices } from "$services";
import { Company, DeliveryType } from "$types";
import { AssignCarrierFormValues, CompanyTypeField } from "../types";
import useCompanyOptions from "$components/ShippingRecord/useCompanyOptions";
import useCompanyForm from "$hooks/useCompanyForm";

interface UseAssignCarrierLogicReturn {
  form: ReturnType<typeof useForm<AssignCarrierFormValues>>;
  selectedCarrierCompany: Company | undefined;
  isCommissionCompanySelectDisplayed: boolean;
  isCarrierCompanySelectDisplayed: boolean;
  isAgencyCompanySelectDisplayed: boolean;
  isCommissionCompanySelectRequired: boolean;
  isCarrierCompanySelectRequired: boolean;
  isAgencyCompanySelectRequired: boolean;
  agencyOptions: any[];
  carrierOptions: any[];
  refetchCompanies: () => Promise<void>;
  handleAddCompanyClick: (companyTypeField: CompanyTypeField) => void;
  handleOnCompanyCreated: (
    companyId: string,
    companyForm: Company
  ) => Promise<void>;
  selectedCompanyTypeToCreate: CompanyTypeField;
  setSelectedCompanyTypeToCreate: (type: CompanyTypeField) => void;
}

export const useAssignCarrierLogic = (
  defaultValues: AssignCarrierFormValues
): UseAssignCarrierLogicReturn => {
  const form = useForm<AssignCarrierFormValues>({
    mode: "onChange",
    defaultValues,
  });
  const [selectedCompanyTypeToCreate, setSelectedCompanyTypeToCreate] =
    useState<CompanyTypeField>("carrierCompanyId");
  const { watch, reset, resetField } = form;

  // Watch form values
  const deliveryType = watch("deliveryType");
  const carrierCompanyId = watch("carrierCompanyId");

  // Get company options
  const { refetchCompanies, agencyOptions, carrierOptions } = useCompanyOptions(
    deliveryType,
    carrierCompanyId
  );

  // Get selected carrier company
  const { data: selectedCarrierCompany } = useQuery(
    ["getCompanyById", carrierCompanyId],
    () => CompanyServices.getCompanyById(carrierCompanyId),
    {
      enabled: !!carrierCompanyId,
      cacheTime: 5 * 60 * 1000,
      refetchOnMount: false,
      refetchOnWindowFocus: false,
    }
  );

  const isCommissionCompanySelectDisplayed =
    deliveryType && deliveryType === DeliveryType.CHARTERING;
  const isCarrierCompanySelectDisplayed =
    deliveryType && deliveryType !== DeliveryType.PROVISIONING;
  const isAgencyCompanySelectDisplayed = !!(
    deliveryType &&
    deliveryType !== DeliveryType.CHARTERING &&
    deliveryType !== DeliveryType.PROVISIONING &&
    carrierCompanyId
  );
  const isCommissionCompanySelectRequired = false;
  const isCarrierCompanySelectRequired =
    deliveryType === DeliveryType.EXPRESS ||
    deliveryType === DeliveryType.MESSAGING;
  const isAgencyCompanySelectRequired =
    deliveryType !== DeliveryType.CHARTERING &&
    deliveryType !== DeliveryType.PROVISIONING;

  // Handle form resets
  useEffect(() => {
    reset(defaultValues);
  }, [defaultValues, reset]);

  useEffect(() => {
    if (deliveryType !== defaultValues.deliveryType) {
      resetField("carrierCompanyId", { defaultValue: null });
      resetField("commissionCompanyId", { defaultValue: null });
      resetField("agencyCompanyId", { defaultValue: null });
    }
  }, [deliveryType, defaultValues.deliveryType, resetField]);

  const handleOnCompanyCreated = async (
    companyId: string,
    companyForm: Company
  ) => {
    await refetchCompanies();
    const compatibleType = companyForm.activities.some(
      (activity) => activity.name === deliveryType
    );
    if (compatibleType) form.setValue(selectedCompanyTypeToCreate, companyId);
  };

  const { togglePopin: toggleCompanyDrawer } = useCompanyForm(
    handleOnCompanyCreated
  );

  // Handle company creation
  const handleAddCompanyClick = (companyTypeField: CompanyTypeField) => {
    setSelectedCompanyTypeToCreate(companyTypeField);
    toggleCompanyDrawer(true);
  };

  return {
    form,
    selectedCarrierCompany,
    isCommissionCompanySelectDisplayed,
    isCarrierCompanySelectDisplayed,
    isAgencyCompanySelectDisplayed,
    isCommissionCompanySelectRequired,
    isCarrierCompanySelectRequired,
    isAgencyCompanySelectRequired,
    agencyOptions,
    carrierOptions,
    refetchCompanies,
    handleAddCompanyClick,
    handleOnCompanyCreated,
    selectedCompanyTypeToCreate,
    setSelectedCompanyTypeToCreate,
  };
};
